import { useState } from "react";

import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { m } from "@vega-core/paraglide/messages";

import { buildFolderDropdownData, getSiblingNames } from "./utils/treeUtils";
import { FOLDER_INDENTATION_WIDTH, ROOT_FOLDER_VALUE } from "./constants";
import type { NodeMap } from "./types";

type CreateFolderModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  nodeMap: NodeMap;
  initialParentId: string | null;
  onSubmit: (name: string, parentId: string | null) => void;
};

export function CreateFolderModal({
  open,
  setOpen,
  nodeMap,
  initialParentId,
  onSubmit,
}: CreateFolderModalProps) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<string | null>(initialParentId);

  const trimmedName = name.trim();
  const existingNames = getSiblingNames(parentId, "folder", nodeMap);
  const isDuplicate = trimmedName.length > 0 && existingNames.has(trimmedName);

  const {
    depthMap,
    options: dropdownOptions,
    pathMap,
  } = buildFolderDropdownData(nodeMap);

  function handleClose() {
    setOpen(false);
  }

  function handleSubmit() {
    if (!trimmedName || isDuplicate) return;

    const nameToSubmit = trimmedName;
    setName("");
    onSubmit(nameToSubmit, parentId);
    handleClose();
  }

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: handleSubmit,
        mainActionText: m.coInvestments_dataRoom_createFolder(),
        disabled: !trimmedName || isDuplicate,
        secondaryAction: handleClose,
        secondaryActionText: m.common_cancel(),
      }}
      dataTestId="create-folder-modal"
      defaultWidth
      fluidHeight
      open={open}
      setOpen={(val) => {
        if (!val) handleClose();
      }}
      title={m.coInvestments_dataRoom_createFolder()}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <InputControl
          autoFocus
          error={
            isDuplicate
              ? m.coInvestments_dataRoom_duplicateFolderName()
              : undefined
          }
          label={m.common_name()}
          name="create-folder-name"
          onChange={(event) => setName(event.target.value)}
          placeholder={m.coInvestments_dataRoom_namePlaceholder()}
          type="text"
          value={name}
          withBorder
        />
        <ThemedDropdown
          isClearable={false}
          label={m.coInvestments_dataRoom_parentFolder()}
          name="create-folder-parent"
          onChange={(opt) => {
            setParentId(
              opt === undefined || opt.value === ROOT_FOLDER_VALUE
                ? null
                : opt.value,
            );
          }}
          options={dropdownOptions}
          renderOption={(option) => {
            const depth = depthMap.get(option.value) ?? 0;

            return (
              <span
                style={{ paddingLeft: `${depth * FOLDER_INDENTATION_WIDTH}px` }}
              >
                {option.label}
              </span>
            );
          }}
          renderValue={() => {
            const key = parentId ?? ROOT_FOLDER_VALUE;
            const path = pathMap.get(key);

            return (
              <span className="min-w-0 truncate">
                {path || m.coInvestments_dataRoom_root()}
              </span>
            );
          }}
          value={parentId ?? ROOT_FOLDER_VALUE}
        />
      </form>
    </ActionaryModal>
  );
}
